import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Row,
} from "reactstrap";
import secureLocalStorage from "react-secure-storage";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { PageSubHeader } from "components/CommonLayouts/PageSubHeader";
import Footer from "components/VerticalLayout/Footer";
import { get, post, put, uri } from "helpers/api_helper";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import DragAndDropComponent from "components/FileUploadDrag/DragAndDrop";
import * as api from "../../../constants/api_constants";
import { charactersOnly, zipcode } from "helpers/validations/regex";
import { charactersWithSpaces } from "helpers/validations/regex";
import { showErrorToast, showSuccessToast } from "helpers/toastMessages";
import { CustomButton } from "components/CommonInputs/CustomButtons/CustomButton";
import { getAllCountries, getStatesForCountry, getCitiesForState } from "helpers/utils/locationUtil";
import { getUserModulePermissions } from "helpers/rolebased_helper";

const identificationTypes = [
  { id: 1, name: "Aadhar Card Number", value: "AADHAR_CARD_NUMBER" },
  { id: 2, name: "Driving License", value: "DRIVING_LICENSE" },
  { id: 3, name: "Green Card", value: "GREEN_CARD" },
  { id: 4, name: "H1B I-797", value: "H1B_I797" },
  { id: 5, name: "PAN Card Number", value: "PAN_CARD_NUMBER" },
  { id: 6, name: "Passport Number", value: "PASSPORT_NUMBER" },
  { id: 7, name: "Permanent Residence", value: "PERMANENT_RESIDENCE" },
  { id: 8, name: "Social Security Number (SSN)", value: "SSN" },
];

const employmentTypes = [
  { id: 1, name: "C2H (Contract to Hire)", value: "C2H", rank: 1 },
  { id: 2, name: "Consultant", value: "CONSULTANT", rank: 2 },
  { id: 3, name: "Contractor", value: "CONTRACTOR", rank: 3 },
  { id: 4, name: "Full Time", value: "FULL_TIME", rank: 4 },
  { id: 5, name: "Intern", value: "INTERN", rank: 5 },
  { id: 6, name: "Part Time", value: "PART_TIME", rank: 6 },
  { id: 7, name: "Temporary", value: "TEMPORARY", rank: 7 },
];

const sources = [
  "Twitter",
  "Indeed",
  "ZipRecruiter",
  "Monster",
  "CareerBuilder",
  "Naukri",
  "Shine",
  "Foundit",
  "StepStone",
  "TotalJobs",
  "Reed",
  "InfoJobs",
  "LinkedIn",
  "Glassdoor",
  "SimplyHired",
  "Bayt",
  "Naukrigulf",
  "GulfTalen",
  "JobStreet",
  "JobsDB",
  "FastJobs",
];

const designationOptions = {
  "Software Development & Engineering": [
    "Chief Technology Officer (CTO)",
    "VP of Engineering",
    "Software Architect",
    "Engineering Manager",
    "Lead Software Engineer",
    "Senior Software Engineer",
    "Software Engineer",
    "Junior Software Engineer",
    "Frontend Developer",
    "Backend Developer",
    "Full Stack Developer",
    "Mobile App Developer",
    "DevOps Engineer",
    "others",
  ],
  "Product Management": [
    "Chief Product Officer (CPO)",
    "VP of Product",
    "Product Manager",
    "Associate Product Manager",
    "Product Owner",
    "others",
  ],
  "Quality Assurance (QA) & Testing": [
    "QA Manager",
    "Senior QA Engineer",
    "QA Engineer",
    "Automation Tester",
    "Manual Tester",
    "others",
  ],
  "UI/UX Design": [
    "UI/UX Director",
    "UX Designer",
    "UI Designer",
    "Graphic Designer",
    "Interaction Designer",
    "others",
  ],
  "IT & Infrastructure": [
    "Chief Information Officer (CIO)",
    "IT Manager",
    "System Administrator",
    "Network Engineer",
    "IT Support Specialist",
    "others",
  ],
  "Data Science & Analytics": [
    "Data Scientist",
    "Data Engineer",
    "Data Analyst",
    "Business Intelligence Analyst",
    "others",
  ],
  Cybersecurity: [
    "Chief Information Security Officer (CISO)",
    "Security Engineer",
    "Security Analyst",
    "Ethical Hacker",
    "others",
  ],
  "Sales & Business Development": [
    "Chief Revenue Officer (CRO)",
    "VP of Sales",
    "Sales Manager",
    "Business Development Manager",
    "Account Executive",
    "Sales Representative",
    "others",
  ],
  "Marketing & Digital Growth": [
    "Chief Marketing Officer (CMO)",
    "VP of Marketing",
    "Digital Marketing Manager",
    "SEO Specialist",
    "Content Marketer",
    "Social Media Manager",
    "others",
  ],
  "Human Resources (HR)": [
    "Chief Human Resources Officer (CHRO)",
    "HR Manager",
    "Talent Acquisition Specialist",
    "HR Executive",
    "others",
  ],
  "Finance & Accounting": [
    "Chief Financial Officer (CFO)",
    "Finance Manager",
    "Accountant",
    "Financial Analyst",
    "others",
  ],
  "Customer Support & Success": [
    "VP of Customer Success",
    "Customer Support Manager",
    "Technical Support Engineer",
    "Customer Success Manager",
    "others",
  ],
};

function AddEmployee() {
  const navigate = useNavigate();
  const { empId } = useParams();
  const [fetchedData, setFetchedData] = useState({});
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [CASstates, setCAStates] = useState([]);
  const [CACities, setCACities] = useState([]);
  const [minimumDate, setMinimumDate] = useState(new Date());
  const [employees, setEmployees] = useState([]);
  const [uploadedDocs, setUploadedDocs] = useState({
    resume: {},
    educationalCertificates: {},
    nationalIdentification: {},
    cancelledCheckbook: {},
  });
  const [companyFiles, setCompanyFiles] = useState([
    {
      experienceLetter: {},
      relieving: {},
    },
  ]);
  const [selectedDepartment, setSelectedDepartment] = useState("");

  const handleFileUpload = (response, documentType) => {
    setUploadedDocs((prev) => ({
      ...prev,
      [documentType]: response,
    }));
  };

  const storedUser = secureLocalStorage.getItem("authUser");
  const user = JSON.parse(storedUser);

  const fetchEmployeesData = async (empId) => {
    try {
      const response = await get(api.GET_EMPLOYEE_BY_ID(empId));
      const empData = response?.data;

      const employeeDoc = empData?.documents?.[0] || {};
      const fileIds = [
        { key: "educationalCertificates", id: employeeDoc.educationalCertificates },
        { key: "resume", id: employeeDoc.resume },
        { key: "nationalIdentification", id: employeeDoc.nationalIdentification },
        { key: "cancelledCheckbook", id: employeeDoc.cancelledCheque },
        { key: "experienceLetter", id: employeeDoc.experienceLetter },
        { key: "relieving", id: employeeDoc.relievingLetter },
      ].filter((item) => item.id);

      const fileDetails = await Promise.all(
        fileIds.map(async (ids) => {
          try {
            const fileResponse = await get(`${uri}/fileservice/api/files/view/${ids.id}`);
            return {
              key: ids.key,
              file: {
                id: fileResponse.id,
                fileName: fileResponse.fileName,
                name: fileResponse.fileName,
                path: fileResponse.path || `/Uploads/${fileResponse.fileName}`,
              },
            };
          } catch (error) {
            console.log(`Error fetching file ${ids.id}:`, error);
            return { key: ids.key, file: null };
          }
        })
      );

      const newUploadedDocs = { ...uploadedDocs };
      const newCompanyFiles = [{ ...companyFiles[0] }];

      fileDetails.forEach(({ key, file }) => {
        if (!file) return;
        if (["resume", "cancelledCheckbook", "nationalIdentification", "educationalCertificates"].includes(key)) {
          newUploadedDocs[key] = file;
        }
        if (["experienceLetter", "relieving"].includes(key)) {
          newCompanyFiles[0][key] = file;
        }
      });

      setUploadedDocs(newUploadedDocs);
      setCompanyFiles(newCompanyFiles);

      const department = empData?.department || "";
      setSelectedDepartment(department);

      const employeeDesignation = empData?.designation || "";
      const isValidDesignation = department && designationOptions[department]?.includes(employeeDesignation);

      const permanentAddress = empData?.residence?.[0]?.permanentAddress || {};
      const communicationAddress = empData?.residence?.[0]?.communicationAddress || {};
      const isSameAddress =
        permanentAddress.country === communicationAddress.country &&
        permanentAddress.province === communicationAddress.province &&
        permanentAddress.city === communicationAddress.city &&
        permanentAddress.zipCode === communicationAddress.zipCode &&
        permanentAddress.address === communicationAddress.address;

      setFetchedData({
        employeeId: empData.employeeId,
        employeeCodeId: empData?.employeeCode,
        employeeFirstName: empData?.firstName || "",
        employeeMiddleName: empData?.middleName || "",
        employeeLastName: empData?.lastName || "",
        employeeEmail: empData?.email || "",
        employeeMobile: empData?.mobile || "",
        employeeDesignation: isValidDesignation ? employeeDesignation : employeeDesignation ? "others" : "",
        employeeRole: empData?.role || "",
        identificationNumber: empData?.identificationNumber || "",
        identificationType: empData?.identificationType || "",
        employmentType: empData?.employmentType || "",
        exitDate: empData?.employeeExitDate?.slice(0, 10) || "",
        exitReason: empData?.exitReason || "",
        hireDate: empData?.hireDate?.slice(0, 10) || "",
        department: empData?.department || "",
        reportingTo: empData?.reportingTo || "",
        source: empData?.source || "",
        probationEndDate: empData?.probationEndDate?.slice(0, 10) || "",
        noticePeriod: empData?.noticePeriod || "",
        status: empData?.status !== undefined ? Number(empData.status) : 1,
        personalInformationId: empData?.personalInformation[0]?.id || "",
        dateOfBirth: empData?.personalInformation?.[0]?.dateOfBirth?.slice(0, 10) || "",
        gender: empData?.personalInformation?.[0]?.gender || "",
        maritalStatus: empData?.personalInformation?.[0]?.maritalStatus || "",
        bloodGroup: empData?.personalInformation?.[0]?.bloodGroup || "",
        residenceId: empData?.residence[0]?.id,
        country: empData?.residence?.[0]?.permanentAddress?.country || "",
        zipCode: empData?.residence?.[0]?.permanentAddress?.zipCode || "",
        province: empData?.residence?.[0]?.permanentAddress?.province || "",
        city: empData?.residence?.[0]?.permanentAddress?.city || "",
        address: empData?.residence?.[0]?.permanentAddress?.address || "",
        CAcountry: empData?.residence?.[0]?.communicationAddress?.country || "",
        CAzipCode: empData?.residence?.[0]?.communicationAddress?.zipCode || "",
        CAprovince: empData?.residence?.[0]?.communicationAddress?.province || "",
        CAcity: empData?.residence?.[0]?.communicationAddress?.city || "",
        CAaddress: empData?.residence?.[0]?.communicationAddress?.address || "",
        emergencyContactsId: empData?.emergencyContacts?.[0]?.id || "",
        contactPersonName: empData?.emergencyContacts?.[0]?.contactPersonName || "",
        contactMobile: empData?.emergencyContacts?.[0]?.mobile || "",
        relation: empData?.emergencyContacts?.[0]?.relation || "",
        documentsId: empData?.documents?.[0]?.id || "",
        documentCategory: empData?.documents?.[0]?.documentCategory || "EDUCATION",
        documentName: empData?.documents?.[0]?.documentName || "BSc Computer Science",
        filePath: empData?.documents?.[0]?.filePath || "/documents/bsc-cs.pdf",
        fileType: empData?.documents?.[0]?.fileType || "PDF",
        fileSize: empData?.documents?.[0]?.fileSize || 50000,
        socialProfileId: empData?.socialProfile?.[0]?.id || "",
        linkedIn: empData?.socialProfile?.[0]?.linkedIn || "",
        facebook: empData?.socialProfile?.[0]?.facebook || "",
        twitter: empData?.socialProfile?.[0]?.twitter || "",
        rolesId: empData?.rolesAndResponsibilities?.[0]?.id || "",
        roles: empData?.rolesAndResponsibilities?.[0].wordTypeBox || "",
        sameAddress: isSameAddress ? "YES" : "NO",
      });
    } catch (error) {
      console.error("Error fetching employee data:", error);
      showErrorToast("Failed to fetch employee data");
    }
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: empId
      ? fetchedData
      : {
          employeeFirstName: "",
          employeeMiddleName: "",
          employeeCodeId: "",
          employeeLastName: "",
          employeeEmail: "",
          employeeMobile: "",
          employeeDesignation: "",
          customDesignation: "",
          employeeRole: "",
          identificationNumber: "",
          identificationType: "",
          employmentType: "",
          exitDate: "",
          exitReason: "",
          hireDate: "",
          department: "",
          reportingTo: "",
          source: "",
          probationEndDate: "",
          noticePeriod: "",
          status: 1, // Default to Active for new employees
          dateOfBirth: "",
          gender: "",
          maritalStatus: "",
          bloodGroup: "",
          country: "",
          zipCode: "",
          province: "",
          city: "",
          address: "",
          CAcountry: "",
          CAzipCode: "",
          CAprovince: "",
          CAcity: "",
          CAaddress: "",
          contactPersonName: "",
          contactMobile: "",
          relation: "",
          documentCategory: "EDUCATION",
          documentName: "BSc Computer Science",
          filePath: "/documents/bsc-cs.pdf",
          fileType: "PDF",
          fileSize: 50000,
          linkedIn: "",
          facebook: "",
          twitter: "",
          roles: "",
          sameAddress: "NO",
        },
    validationSchema: Yup.object({
      employeeFirstName: Yup.string()
        .required("Please enter employee first name")
        .matches(charactersOnly, "First Name should only contain characters"),
      employeeMiddleName: Yup.string(),
      employeeLastName: Yup.string()
        .required("Please enter employee last name")
        .matches(charactersOnly, "Last Name should only contain characters"),
      employeeCodeId: Yup.string().required("Please enter employee id"),
      employeeEmail: Yup.string()
        .email("Invalid email Address")
        .required("Please enter email"),
      employeeMobile: Yup.string()
        .required("Primary contact phone is required")
        .test("isValidPhone", "Invalid phone number format", (value) => !value || isValidPhoneNumber(value))
        .test("startsWithSixOrHigher", "Phone number must start with 6, 7, 8, or 9", (value) => {
          if (!value) return true;
          const digitsOnly = value.replace(/\D/g, "");
          const localNumber = digitsOnly.startsWith("91") ? digitsOnly.slice(2) : digitsOnly;
          return /^[6-9]\d{9}$/.test(localNumber);
        }),
      employeeDesignation: Yup.string().required("Please select designation"),
      status: Yup.number().required("Please select status").oneOf([0, 1], "Status must be Active (1) or In-Active (0)"),
      exitDate: Yup.date().when("status", {
        is: (status) => Number(status) === 0,
        then: (schema) =>
          schema
            .required("Please enter exit date")
            .test("is-after-hire-date", "Exit date must be after hire date", function (value) {
              const { hireDate } = this.parent;
              return !value || !hireDate || new Date(value) > new Date(hireDate);
            }),
        otherwise: (schema) => schema.nullable(),
      }),
      exitReason: Yup.string().when("status", {
        is: (status) => Number(status) === 0,
        then: (schema) => schema.required("Please enter exit reason"),
        otherwise: (schema) => schema.nullable(),
      }),
      employmentType: Yup.string().required("Please select employment type"),
      employeeRole: Yup.string().matches(/^[a-zA-Z0-9]*$/, "Primary Contact can only contain letters, numbers, spaces."),
      hireDate: Yup.date().required("Please enter hire date"),
      department: Yup.string().required("Please select department"),
      reportingTo: Yup.string().required("Please select reporting to"),
      source: Yup.string(),
      probationEndDate: Yup.date()
        .required("Please enter probation end date")
        .test("is-after-hire-date", "Probation end date must be after hire date", function (value) {
          const { hireDate } = this.parent;
          return !value || !hireDate || new Date(value) > new Date(hireDate);
        }),
      noticePeriod: Yup.number().positive("Notice period must be positive"),
      dateOfBirth: Yup.date()
        .max(minimumDate, "You must be at least 15 years old")
        .required("Please enter date of birth"),
      gender: Yup.string().required("Please select gender"),
      maritalStatus: Yup.string().required("Please select marital status"),
      bloodGroup: Yup.string().required("Please select blood group"),
      country: Yup.string().required("Please select country"),
      zipCode: Yup.string().required("Please enter zip code").matches(zipcode, "Enter a valid zipcode"),
      province: Yup.string().required("Please select province"),
      CAcountry: Yup.string().required("Please select Communication Address country"),
      CAzipCode: Yup.string().required("Please enter Communication Address zip code").matches(zipcode, "Enter a valid zipcode"),
      CAprovince: Yup.string().required("Please select Communication Address province"),
      contactPersonName: Yup.string()
        .required("Please enter contact person name")
        .matches(charactersWithSpaces, "Name should contain characters only"),
      contactMobile: Yup.string()
        .required("Contact phone is required")
        .test("isValidPhone", "Invalid phone number format", (value) => !value || isValidPhoneNumber(value))
        .test("startsWithSixOrHigher", "Phone number must start with 6, 7, 8, or 9", (value) => {
          if (!value) return false;
          const digitsOnly = value.replace(/\D/g, "");
          const localNumber = digitsOnly.startsWith("91") ? digitsOnly.slice(2) : digitsOnly;
          return /^[6-9]\d{9}$/.test(localNumber);
        }),
      identificationType: Yup.string().required("Identification Type is required"),
      identificationNumber: Yup.string().test(
        "identification-number-validation",
        "Identification Number is required",
        function (value) {
          const { identificationType } = this.parent;
          if (
            [
              "AADHAR_CARD_NUMBER",
              "DRIVING_LICENSE",
              "GREEN_CARD",
              "H1B_I797",
              "PAN_CARD_NUMBER",
              "PASSPORT_NUMBER",
              "PERMANENT_RESIDENCE",
              "SSN",
            ].includes(identificationType)
          ) {
            let regex;
            let errorMessage;
            switch (identificationType) {
              case "AADHAR_CARD_NUMBER":
                regex = /^\d{12}$/;
                errorMessage = "Aadhaar number must be 12 digits";
                break;
              case "DRIVING_LICENSE":
                regex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z0-9]{10,16}$/;
                errorMessage = "Driving license must be 10–16 alphanumeric characters and contain both letters and numbers";
                break;
              case "GREEN_CARD":
                regex = /^[A-Za-z0-9]{13,20}$/;
                errorMessage = "Green card must be 13-20 alphanumeric characters";
                break;
              case "H1B_I797":
                regex = /^[A-Za-z0-9]{13,20}$/;
                errorMessage = "H1B I-797 must be 13-20 alphanumeric characters";
                break;
              case "PAN_CARD_NUMBER":
                regex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
                errorMessage = "PAN card number must be in format: 5 letters, 4 digits, 1 letter (e.g. ABCDE1234F)";
                break;
              case "PASSPORT_NUMBER":
                regex = /^[A-Za-z0-9]{8,15}$/;
                errorMessage = "Passport number must be 8-15 alphanumeric characters";
                break;
              case "PERMANENT_RESIDENCE":
                regex = /^[A-Za-z0-9]{8,50}$/;
                errorMessage = "Permanent residence ID must be 8-50 alphanumeric characters";
                break;
              case "SSN":
                regex = /^\d{9}$/;
                errorMessage = "SSN must be 9 digits";
                break;
              default:
                regex = /^$/;
                errorMessage = "Invalid identification number format";
            }
            if (!value || !regex.test(value)) {
              return this.createError({ message: errorMessage });
            }
          }
          return true;
        }
      ),
    }),
    onSubmit: async (values) => {
      const payload = {
        subscriptionId: 100,
        firstName: values.employeeFirstName,
        middleName: values.employeeMiddleName,
        lastName: values.employeeLastName,
        email: values.employeeEmail,
        employeeCode: values.employeeCodeId,
        mobile: values.employeeMobile,
        designation: values.employeeDesignation === "others" ? values.customDesignation : values.employeeDesignation,
        role: values.employeeRole,
        identificationNumber: values.identificationNumber,
        identificationType: values.identificationType,
        employmentType: values.employmentType,
        hireDate: values.hireDate,
        department: values.department,
        reportingTo: values.reportingTo,
        source: values.source,
        probationEndDate: values.probationEndDate,
        noticePeriod: values.noticePeriod,
        createdBy: user.email,
        status: Number(values.status),
        employeeExitDate: values.exitDate || null,
        exitReason: values.exitReason || null,
        delFlag: 1,
        personalInformation: [
          {
            dateOfBirth: values.dateOfBirth,
            gender: values.gender,
            maritalStatus: values.maritalStatus,
            bloodGroup: values.bloodGroup,
          },
        ],
        residence: [
          {
            permanentAddress: {
              country: values.country,
              zipCode: values.zipCode,
              province: values.province,
              city: values.city,
              address: values.address,
            },
            communicationAddress: {
              country: values.CAcountry,
              zipCode: values.CAzipCode,
              province: values.CAprovince,
              city: values.CAcity,
              address: values.CAaddress,
            },
          },
        ],
        emergencyContacts: [
          {
            contactPersonName: values.contactPersonName,
            mobile: values.contactMobile,
            relation: values.relation,
          },
        ],
        documents: [
          {
            resume: uploadedDocs?.resume?.id ? String(uploadedDocs.resume.id) : "",
            relievingLetter: companyFiles[0]?.relieving?.id ? String(companyFiles[0].relieving.id) : "",
            experienceLetter: companyFiles[0]?.experienceLetter?.id ? String(companyFiles[0].experienceLetter.id) : "",
            nationalIdentification: uploadedDocs?.nationalIdentification?.id ? String(uploadedDocs.nationalIdentification.id) : "",
            educationalCertificates: uploadedDocs?.educationalCertificates?.id ? String(uploadedDocs.educationalCertificates.id) : "",
            cancelledCheque: uploadedDocs?.cancelledCheckbook?.id ? String(uploadedDocs.cancelledCheckbook.id) : "",
          },
        ],
        socialProfile: [
          {
            linkedIn: values.linkedIn,
            facebook: values.facebook,
            twitter: values.twitter,
          },
        ],
        rolesAndResponsibilities: [
          {
            wordTypeBox: values.roles,
          },
        ],
      };

      const payloadUpdate = {
        ...payload,
        employeeId: fetchedData.employeeId,
        updatedBy: user.email,
        personalInformation: [
          {
            id: fetchedData.personalInformationId,
            dateOfBirth: values.dateOfBirth,
            gender: values.gender,
            maritalStatus: values.maritalStatus,
            bloodGroup: values.bloodGroup,
          },
        ],
        residence: [
          {
            id: fetchedData.residenceId,
            permanentAddress: {
              country: values.country,
              zipCode: values.zipCode,
              province: values.province,
              city: values.city,
              address: values.address,
            },
            communicationAddress: {
              country: values.CAcountry,
              zipCode: values.CAzipCode,
              province: values.CAprovince,
              city: values.CAcity,
              address: values.CAaddress,
            },
          },
        ],
        emergencyContacts: [
          {
            id: fetchedData.emergencyContactsId,
            contactPersonName: values.contactPersonName,
            mobile: values.contactMobile,
            relation: values.relation,
          },
        ],
        socialProfile: [
          {
            id: fetchedData.socialProfileId,
            linkedIn: values.linkedIn,
            facebook: values.facebook,
            twitter: values.twitter,
          },
        ],
        rolesAndResponsibilities: [
          {
            id: fetchedData.rolesId,
            wordTypeBox: values.roles,
          },
        ],
      };

      try {
        if (empId) {
          const response = await put(api.UPDATE_EMPLOYEE_BY_ID(empId), payloadUpdate);
          if (response) {
            showSuccessToast("Employee updated successfully");
            navigate("/employees");
          } else {
            showErrorToast(response?.message);
          }
        } else {
          const response = await post(api.CREATE_EMPLOYEE, payload);
          console.log("response",response);
          
          if (response) {
            showSuccessToast("Employee created successfully");
            navigate("/employees");
          } else {
            showErrorToast(response?.message);
          }
        }
      } catch (error) {
        showErrorToast("Something went wrong");
      }
    },
  });

  const handleInput = (e) => {
    e.target.value = e.target.value.replace(/[^0-9]/g, "");
  };

  const handleAddress = (e) => {
    const newValue = e.target.checked ? "YES" : "NO";
    validation.setFieldValue("sameAddress", newValue);
    if (newValue === "YES") {
      validation.setFieldValue("country", validation.values.CAcountry);
      validation.setFieldValue("province", validation.values.CAprovince);
      validation.setFieldValue("city", validation.values.CAcity);
      validation.setFieldValue("zipCode", validation.values.CAzipCode);
      validation.setFieldValue("address", validation.values.CAaddress);
    } else {
      validation.setFieldValue("country", "");
      validation.setFieldValue("province", "");
      validation.setFieldValue("city", "");
      validation.setFieldValue("zipCode", "");
      validation.setFieldValue("address", "");
    }
  };

  useEffect(() => {
    const employeeData = async () => {
      try {
        const response = await get(api.GET_ALL_EMPLOYEES_NAME_ID_MAIL);
        const employeeResponse = response.map((item) => ({
          value: item.employeeId,
          label: `${item.firstName} ${item.lastName}`,
          number: item.number,
          email: item.email,
        }));
        setEmployees(employeeResponse);
      } catch (error) {
        console.log("Error fetching employees data");
      }
    };
    employeeData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const fetchedCountries = getAllCountries();
      setCountries(fetchedCountries);
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (empId) {
      fetchEmployeesData(empId);
    }
    const date = new Date();
    date.setFullYear(date.getFullYear() - 15);
    setMinimumDate(date);
  }, [empId]);

  useEffect(() => {
    const fetchedData = async () => {
      const fetchedStates = getStatesForCountry(validation.values.country);
      setStates(fetchedStates);
    };
    if (validation.values.country) {
      fetchedData();
    }
  }, [validation.values.country]);

  useEffect(() => {
    const fetchedData = async () => {
      const fetchedStates = getStatesForCountry(validation.values.CAcountry);
      setCAStates(fetchedStates);
    };
    if (validation.values.CAcountry) {
      fetchedData();
    }
  }, [validation.values.CAcountry]);

  useEffect(() => {
    const fetchedData = async () => {
      const fetchedCities = getCitiesForState(validation.values.CAcountry, validation.values.CAprovince);
      setCACities(fetchedCities);
    };
    if (validation.values.CAprovince) {
      fetchedData();
    }
  }, [validation.values.CAprovince, validation.values.CAcountry]);

  useEffect(() => {
    const fetchedData = async () => {
      const fetchedCities = getCitiesForState(validation.values.country, validation.values.province);
      setCities(fetchedCities);
    };
    if (validation.values.province) {
      fetchedData();
    }
  }, [validation.values.province, validation.values.country]);

  return (
    <div className="page-content">
      <Container fluid={true} className="py-0 px-0">
        <PageSubHeader
          heading={empId ? "Update Employee" : "Add Employee"}
          routePath={"Home"}
          pageName={empId ? "Update Employee" : "Add Employee"}
        />
        <Row className="px-4 mx-0 my-3">
          <Col className="p-0 m-0" xs={12} md={12}>
            <Card>
              <CardBody>
                <Form
                  className="row g-3 needs-validation py-3"
                  onSubmit={(e) => {
                    e.preventDefault();
                    validation.handleSubmit();
                    return false;
                  }}
                >
                  <Row className="card-group">
                    <Col xs={12} className="card-header">
                      <p className="form-header">Employee Information</p>
                    </Col>
                    <Row className="card-group-body">
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="employeeFirstName">First Name <span className="text-danger">*</span></Label>
                          <Input
                            name="employeeFirstName"
                            type="text"
                            className="form-control"
                            id="employeeFirstName"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.employeeFirstName || ""}
                            invalid={validation.touched.employeeFirstName && validation.errors.employeeFirstName}
                          />
                          {validation.touched.employeeFirstName && validation.errors.employeeFirstName ? (
                            <FormFeedback type="invalid">{validation.errors.employeeFirstName}</FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="employeeMiddleName">Middle Name</Label>
                          <Input
                            name="employeeMiddleName"
                            type="text"
                            className="form-control"
                            id="employeeMiddleName"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.employeeMiddleName || ""}
                            invalid={validation.touched.employeeMiddleName && validation.errors.employeeMiddleName}
                          />
                          {validation.touched.employeeMiddleName && validation.errors.employeeMiddleName ? (
                            <FormFeedback type="invalid">{validation.errors.employeeMiddleName}</FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="employeeLastName">Last Name <span className="text-danger">*</span></Label>
                          <Input
                            name="employeeLastName"
                            type="text"
                            className="form-control"
                            id="employeeLastName"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.employeeLastName || ""}
                            invalid={validation.touched.employeeLastName && validation.errors.employeeLastName}
                          />
                          {validation.touched.employeeLastName && validation.errors.employeeLastName ? (
                            <FormFeedback type="invalid">{validation.errors.employeeLastName}</FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="employeeCodeId">Employee Id <span className="text-danger">*</span></Label>
                          <Input
                            name="employeeCodeId"
                            type="text"
                            className="form-control"
                            id="employeeCodeId"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.employeeCodeId || ""}
                            invalid={validation.touched.employeeCodeId && validation.errors.employeeCodeId}
                          />
                          {validation.touched.employeeCodeId && validation.errors.employeeCodeId ? (
                            <FormFeedback type="invalid">{validation.errors.employeeCodeId}</FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="employeeEmail">Email <span className="text-danger">*</span></Label>
                          <Input
                            name="employeeEmail"
                            type="text"
                            className="form-control"
                            id="employeeEmail"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.employeeEmail || ""}
                            invalid={validation.touched.employeeEmail && validation.errors.employeeEmail}
                          />
                          {validation.touched.employeeEmail && validation.errors.employeeEmail ? (
                            <FormFeedback type="invalid">{validation.errors.employeeEmail}</FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="employeeMobile">Mobile <span className="text-danger">*</span></Label>
                          <PhoneInput
                            international
                            defaultCountry="IN"
                            className="form-control d-flex"
                            name="employeeMobile"
                            id="employeeMobile"
                            onChange={(e) => validation.setFieldValue("employeeMobile", e)}
                            onBlur={validation.handleBlur}
                            value={validation.values.employeeMobile || ""}
                            onInput={handleInput}
                            invalid={validation.touched.employeeMobile && validation.errors.employeeMobile}
                          />
                          {validation.touched.employeeMobile && validation.errors.employeeMobile ? (
                            <div className="phonenumber-invalid">{validation.errors.employeeMobile}</div>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="identificationType">Identification Type <span className="text-danger">*</span></Label>
                          <Input
                            type="select"
                            name="identificationType"
                            className="form-select"
                            id="identificationType"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.identificationType || ""}
                            invalid={validation.touched.identificationType && validation.errors.identificationType}
                          >
                            <option value="">Select</option>
                            {identificationTypes.map((each) => (
                              <option key={each.id + each.name} value={each.value}>
                                {each.name ?? ""}
                              </option>
                            ))}
                          </Input>
                          {validation.touched.identificationType && validation.errors.identificationType ? (
                            <FormFeedback type="invalid">{validation.errors.identificationType}</FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      {validation.values.identificationType && (
                        <Col md="4">
                          <FormGroup className="mb-3">
                            <Label htmlFor="identificationNumber">
                              {identificationTypes.find(type => type.value === validation.values.identificationType)?.name || "Identification Number"} <span className="text-danger">*</span>
                            </Label>
                            <Input
                              name="identificationNumber"
                              type="text"
                              className="form-control"
                              id="identificationNumber"
                              value={validation.values.identificationNumber || ""}
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              invalid={validation.touched.identificationNumber && validation.errors.identificationNumber}
                            />
                            {validation.touched.identificationNumber && validation.errors.identificationNumber ? (
                              <FormFeedback type="invalid">{validation.errors.identificationNumber}</FormFeedback>
                            ) : null}
                          </FormGroup>
                        </Col>
                      )}
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="department">Department <span className="text-danger">*</span></Label>
                          <Input
                            type="select"
                            name="department"
                            className="form-select"
                            id="department"
                            onChange={(e) => {
                              validation.handleChange(e);
                              setSelectedDepartment(e.target.value);
                              validation.setFieldValue("employeeDesignation", "");
                              validation.setFieldValue("customDesignation", "");
                            }}
                            onBlur={validation.handleBlur}
                            value={validation.values.department}
                            invalid={validation.touched.department && validation.errors.department}
                          >
                            <option value="">Select</option>
                            {Object.keys(designationOptions).map((dept) => (
                              <option key={dept} value={dept}>
                                {dept}
                              </option>
                            ))}
                          </Input>
                          {validation.touched.department && validation.errors.department ? (
                            <FormFeedback type="invalid">{validation.errors.department}</FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="employeeDesignation">Designation <span className="text-danger">*</span></Label>
                          <Input
                            type="select"
                            name="employeeDesignation"
                            className="form-select"
                            id="employeeDesignation"
                            onChange={(e) => {
                              validation.handleChange(e);
                              if (e.target.value !== "others") {
                                validation.setFieldValue("customDesignation", "");
                              }
                            }}
                            onBlur={validation.handleBlur}
                            value={validation.values.employeeDesignation || ""}
                            invalid={validation.touched.employeeDesignation && validation.errors.employeeDesignation}
                          >
                            <option value="">Select</option>
                            {(designationOptions[selectedDepartment] || []).map((designation) => (
                              <option key={designation} value={designation}>
                                {designation}
                              </option>
                            ))}
                          </Input>
                          {validation.touched.employeeDesignation && validation.errors.employeeDesignation ? (
                            <FormFeedback type="invalid">{validation.errors.employeeDesignation}</FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      {validation.values.employeeDesignation === "others" && (
                        <Col md="4">
                          <FormGroup className="mb-3">
                            <Label htmlFor="customDesignation">Custom Designation</Label>
                            <Input
                              name="customDesignation"
                              type="text"
                              className="form-control"
                              id="customDesignation"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.customDesignation || ""}
                            />
                          </FormGroup>
                        </Col>
                      )}
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="employmentType">Employment Type <span className="text-danger">*</span></Label>
                          <Input
                            type="select"
                            name="employmentType"
                            className="form-select"
                            id="employmentType"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.employmentType || ""}
                            invalid={validation.touched.employmentType && validation.errors.employmentType}
                          >
                            <option value="">Select</option>
                            {employmentTypes.map((each) => (
                              <option key={each.id + each.name} value={each.value}>
                                {each.name ?? ""}
                              </option>
                            ))}
                          </Input>
                          {validation.touched.employmentType && validation.errors.employmentType ? (
                            <FormFeedback type="invalid">{validation.errors.employmentType}</FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="reportingTo">Reporting To <span className="text-danger">*</span></Label>
                          <Input
                            type="select"
                            name="reportingTo"
                            className="form-select"
                            id="reportingTo"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.reportingTo || ""}
                            invalid={validation.touched.reportingTo && validation.errors.reportingTo}
                          >
                            <option value="">Select an employee</option>
                            {employees.map((emp) => (
                              <option key={emp.value} value={emp.value}>
                                {emp.label}
                              </option>
                            ))}
                          </Input>
                          {validation.touched.reportingTo && validation.errors.reportingTo ? (
                            <FormFeedback type="invalid">{validation.errors.reportingTo}</FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="source">Source</Label>
                          <Input
                            type="select"
                            name="source"
                            className="form-select"
                            id="source"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.source || ""}
                            invalid={validation.touched.source && validation.errors.source}
                          >
                            <option value="">Select</option>
                            {sources.map((source, index) => (
                              <option key={index} value={source}>
                                {source}
                              </option>
                            ))}
                          </Input>
                          {validation.touched.source && validation.errors.source ? (
                            <FormFeedback type="invalid">{validation.errors.source}</FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="hireDate">Hire Date <span className="text-danger">*</span></Label>
                          <Input
                            name="hireDate"
                            type="date"
                            className="form-control"
                            id="hireDate"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.hireDate || ""}
                            invalid={validation.touched.hireDate && validation.errors.hireDate}
                          />
                          {validation.touched.hireDate && validation.errors.hireDate ? (
                            <FormFeedback type="invalid">{validation.errors.hireDate}</FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="probationEndDate">Probation End Date <span className="text-danger">*</span></Label>
                          <Input
                            name="probationEndDate"
                            type="date"
                            className="form-control"
                            id="probationEndDate"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.probationEndDate || ""}
                            invalid={validation.touched.probationEndDate && validation.errors.probationEndDate}
                          />
                          {validation.touched.probationEndDate && validation.errors.probationEndDate ? (
                            <FormFeedback type="invalid">{validation.errors.probationEndDate}</FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="noticePeriod">Notice Period (days)</Label>
                          <Input
                            name="noticePeriod"
                            type="text"
                            className="form-control"
                            id="noticePeriod"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.noticePeriod || ""}
                            invalid={validation.touched.noticePeriod && validation.errors.noticePeriod}
                          />
                          {validation.touched.noticePeriod && validation.errors.noticePeriod ? (
                            <FormFeedback type="invalid">{validation.errors.noticePeriod}</FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="exitDate">Exit Date</Label>
                          <Input
                            name="exitDate"
                            type="date"
                            className="form-control"
                            id="exitDate"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.exitDate || ""}
                            invalid={validation.touched.exitDate && validation.errors.exitDate}
                          />
                          {validation.touched.exitDate && validation.errors.exitDate ? (
                            <FormFeedback type="invalid">{validation.errors.exitDate}</FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="exitReason">Exit Reason</Label>
                          <Input
                            name="exitReason"
                            type="text"
                            className="form-control"
                            id="exitReason"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.exitReason || ""}
                            invalid={validation.touched.exitReason && validation.errors.exitReason}
                          />
                          {validation.touched.exitReason && validation.errors.exitReason ? (
                            <FormFeedback type="invalid">{validation.errors.exitReason}</FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="status">Status <span className="text-danger">*</span></Label>
                          <Input
                            type="select"
                            name="status"
                            className="form-select"
                            id="status"
                            onChange={(e) => validation.setFieldValue("status", Number(e.target.value))}
                            onBlur={validation.handleBlur}
                            value={validation.values.status}
                            invalid={validation.touched.status && validation.errors.status}
                          >
                            <option value="">Select</option>
                            <option value={1}>Active</option>
                            <option value={0}>In-Active</option>
                          </Input>
                          {validation.touched.status && validation.errors.status ? (
                            <FormFeedback type="invalid">{validation.errors.status}</FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                    </Row>
                  </Row>
                  <Row className="card-group mt-4">
                    <Col xs={12} className="card-header">
                      <p className="form-header">Personal Information</p>
                    </Col>
                    <Row className="card-group-body">
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="dateOfBirth">Date of Birth <span className="text-danger">*</span></Label>
                          <Input
                            name="dateOfBirth"
                            type="date"
                            className="form-control"
                            id="dateOfBirth"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.dateOfBirth || ""}
                            invalid={validation.touched.dateOfBirth && validation.errors.dateOfBirth}
                          />
                          {validation.touched.dateOfBirth && validation.errors.dateOfBirth ? (
                            <FormFeedback type="invalid">{validation.errors.dateOfBirth}</FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="gender">Gender <span className="text-danger">*</span></Label>
                          <Input
                            type="select"
                            name="gender"
                            className="form-select"
                            id="gender"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.gender || ""}
                            invalid={validation.touched.gender && validation.errors.gender}
                          >
                            <option value="">Select</option>
                            <option value="MALE">Male</option>
                            <option value="FEMALE">Female</option>
                            <option value="OTHER">Others</option>
                          </Input>
                          {validation.touched.gender && validation.errors.gender ? (
                            <FormFeedback type="invalid">{validation.errors.gender}</FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="maritalStatus">Marital status <span className="text-danger">*</span></Label>
                          <Input
                            type="select"
                            name="maritalStatus"
                            className="form-select"
                            id="maritalStatus"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.maritalStatus || ""}
                            invalid={validation.touched.maritalStatus && validation.errors.maritalStatus}
                          >
                            <option value="">Select</option>
                            <option value="MARRIED">Married</option>
                            <option value="SINGLE">Single</option>
                          </Input>
                          {validation.touched.maritalStatus && validation.errors.maritalStatus ? (
                            <FormFeedback type="invalid">{validation.errors.maritalStatus}</FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="bloodGroup">Blood Group <span className="text-danger">*</span></Label>
                          <Input
                            type="select"
                            name="bloodGroup"
                            className="form-select"
                            id="bloodGroup"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.bloodGroup || ""}
                            invalid={validation.touched.bloodGroup && validation.errors.bloodGroup}
                          >
                            <option value="">Select</option>
                            <option value="O_POSITIVE">O+</option>
                            <option value="O_NEGATIVE">O-</option>
                            <option value="A_POSITIVE">A+</option>
                            <option value="A_NEGATIVE">A-</option>
                            <option value="B_POSITIVE">B+</option>
                            <option value="B_NEGATIVE">B-</option>
                            <option value="AB_POSITIVE">AB+</option>
                            <option value="AB_NEGATIVE">AB-</option>
                          </Input>
                          {validation.touched.bloodGroup && validation.errors.bloodGroup ? (
                            <FormFeedback type="invalid">{validation.errors.bloodGroup}</FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                    </Row>
                  </Row>
                  <Row className="card-group mt-4">
                    <Col xs={12} className="card-header">
                      <p className="form-header">Communication Address</p>
                    </Col>
                    <Row className="card-group-body">
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="CAcountry">Country <span className="text-danger">*</span></Label>
                          <Input
                            type="select"
                            name="CAcountry"
                            className="form-select"
                            id="CAcountry"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.CAcountry || ""}
                            invalid={validation.touched.CAcountry && validation.errors.CAcountry}
                          >
                            <option value="">Select</option>
                            {countries.map((country) => (
                              <option key={country.value} value={country.value}>
                                {country.label}
                              </option>
                            ))}
                          </Input>
                          {validation.touched.CAcountry && validation.errors.CAcountry ? (
                            <FormFeedback type="invalid">{validation.errors.CAcountry}</FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="CAprovince">Province <span className="text-danger">*</span></Label>
                          <Input
                            type="select"
                            name="CAprovince"
                            className="form-select"
                            id="CAprovince"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.CAprovince || ""}
                            invalid={validation.touched.CAprovince && validation.errors.CAprovince}
                          >
                            <option value="">Select</option>
                            {CASstates.map((state) => (
                              <option key={state.value} value={state.value}>
                                {state.label}
                              </option>
                            ))}
                          </Input>
                          {validation.touched.CAprovince && validation.errors.CAprovince ? (
                            <FormFeedback type="invalid">{validation.errors.CAprovince}</FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="CAcity">City</Label>
                          <Input
                            name="CAcity"
                            type="select"
                            className="form-control"
                            id="CAcity"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.CAcity || ""}
                            invalid={validation.touched.CAcity && validation.errors.CAcity}
                          >
                            <option value="">Select</option>
                            {CACities.map((city) => (
                              <option key={city.value} value={city.value}>
                                {city.label}
                              </option>
                            ))}
                          </Input>
                          {validation.touched.CAcity && validation.errors.CAcity ? (
                            <FormFeedback type="invalid">{validation.errors.CAcity}</FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="CAzipCode">Zip Code <span className="text-danger">*</span></Label>
                          <Input
                            name="CAzipCode"
                            type="text"
                            className="form-control"
                            id="CAzipCode"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.CAzipCode || ""}
                            invalid={validation.touched.CAzipCode && validation.errors.CAzipCode}
                          />
                          {validation.touched.CAzipCode && validation.errors.CAzipCode ? (
                            <FormFeedback type="invalid">{validation.errors.CAzipCode}</FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="CAaddress">Address</Label>
                          <Input
                            name="CAaddress"
                            type="textarea"
                            className="form-control"
                            id="CAaddress"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.CAaddress || ""}
                            invalid={validation.touched.CAaddress && validation.errors.CAaddress}
                          />
                          {validation.touched.CAaddress && validation.errors.CAaddress ? (
                            <FormFeedback type="invalid">{validation.errors.CAaddress}</FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                    </Row>
                  </Row>
                  <Row xs={12} className="p-0 mt-2">
                    <FormGroup className="mb-3 d-flex align-items-center gap-2">
                      <Input
                        name="sameAddress"
                        type="checkbox"
                        className="form-check-input"
                        id="sameAddress"
                        style={{ width: "20px", height: "20px", borderWidth: "1px", borderColor: "#000", cursor: "pointer" }}
                        checked={validation.values.sameAddress === "YES"}
                        value={validation.values.sameAddress}
                        onChange={handleAddress}
                      />
                      <Label htmlFor="sameAddress" className="m-0 mt-1">
                        Permanent Address Same as Communication Address
                      </Label>
                    </FormGroup>
                  </Row>
                  <Row className="card-group mt-4">
                    <Col xs={12} className="card-header">
                      <p className="form-header">Employee Permanent Address</p>
                    </Col>
                    <Row className="card-group-body">
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="country">Country <span className="text-danger">*</span></Label>
                          <Input
                            type="select"
                            name="country"
                            className="form-select"
                            id="country"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.country || ""}
                            invalid={validation.touched.country && validation.errors.country}
                          >
                            <option value="">Select</option>
                            {countries.map((country) => (
                              <option key={country.value} value={country.value}>
                                {country.label}
                              </option>
                            ))}
                          </Input>
                          {validation.touched.country && validation.errors.country ? (
                            <FormFeedback type="invalid">{validation.errors.country}</FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="province">Province <span className="text-danger">*</span></Label>
                          <Input
                            type="select"
                            name="province"
                            className="form-select"
                            id="province"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.province || ""}
                            invalid={validation.touched.province && validation.errors.province}
                          >
                            <option value="">Select</option>
                            {states.map((state) => (
                              <option key={state.value} value={state.value}>
                                {state.label}
                              </option>
                            ))}
                          </Input>
                          {validation.touched.province && validation.errors.province ? (
                            <FormFeedback type="invalid">{validation.errors.province}</FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="city">City</Label>
                          <Input
                            name="city"
                            type="select"
                            className="form-control"
                            id="city"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.city || ""}
                            invalid={validation.touched.city && validation.errors.city}
                          >
                            <option value="">Select</option>
                            {cities.map((city) => (
                              <option key={city.value} value={city.value}>
                                {city.label}
                              </option>
                            ))}
                          </Input>
                          {validation.touched.city && validation.errors.city ? (
                            <FormFeedback type="invalid">{validation.errors.city}</FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="zipCode">Zip Code <span className="text-danger">*</span></Label>
                          <Input
                            name="zipCode"
                            type="text"
                            className="form-control"
                            id="zipCode"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.zipCode || ""}
                            invalid={validation.touched.zipCode && validation.errors.zipCode}
                          />
                          {validation.touched.zipCode && validation.errors.zipCode ? (
                            <FormFeedback type="invalid">{validation.errors.zipCode}</FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="address">Address</Label>
                          <Input
                            name="address"
                            type="textarea"
                            className="form-control"
                            id="address"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.address || ""}
                            invalid={validation.touched.address && validation.errors.address}
                          />
                          {validation.touched.address && validation.errors.address ? (
                            <FormFeedback type="invalid">{validation.errors.address}</FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                    </Row>
                  </Row>
                  <Row className="card-group mt-4">
                    <Col xs={12} className="card-header">
                      <p className="form-header">Emergency Contacts</p>
                    </Col>
                    <Row className="card-group-body">
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="contactPersonName">Contact Person Name <span className="text-danger">*</span></Label>
                          <Input
                            name="contactPersonName"
                            type="text"
                            className="form-control"
                            id="contactPersonName"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.contactPersonName || ""}
                            invalid={validation.touched.contactPersonName && validation.errors.contactPersonName}
                          />
                          {validation.touched.contactPersonName && validation.errors.contactPersonName ? (
                            <FormFeedback type="invalid">{validation.errors.contactPersonName}</FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="contactMobile">Mobile <span className="text-danger">*</span></Label>
                          <PhoneInput
                            international
                            defaultCountry="IN"
                            name="contactMobile"
                            className="form-control d-flex"
                            id="contactMobile"
                            onChange={(value) => validation.setFieldValue("contactMobile", value || "")}
                            onBlur={() => validation.setFieldTouched("contactMobile", true)}
                            value={validation.values.contactMobile || ""}
                          />
                          {validation.touched.contactMobile && validation.errors.contactMobile ? (
                            <div className="phonenumber-invalid">{validation.errors.contactMobile}</div>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup className="mb-3">
                          <Label htmlFor="relation">Relation</Label>
                          <Input
                            type="select"
                            name="relation"
                            className="form-select"
                            id="relation"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.relation || ""}
                            invalid={validation.touched.relation && validation.errors.relation}
                          >
                            <option value="">Select</option>
                            <option value="CHILD">Child</option>
                            <option value="PARENT">Parent</option>
                            <option value="SPOUSE">Spouse</option>
                            <option value="SIBLING">Sibling</option>
                            <option value="OTHER">Other</option>
                          </Input>
                          {validation.touched.relation && validation.errors.relation ? (
                            <FormFeedback type="invalid">{validation.errors.relation}</FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                    </Row>
                  </Row>
                  <Row className="card-group mt-4">
                    <Col xs={12} className="card-header">
                      <p className="form-header">Documents Upload</p>
                    </Col>
                    <Row className="py-2">
                      <Col md="3">
                        <Label htmlFor="relieving">Relieving</Label>
                        <DragAndDropComponent
                          initialFile={companyFiles[0]?.relieving}
                          handleFileUpload={(response) => setCompanyFiles((prev) => [{ ...prev[0], relieving: response }])}
                        />
                      </Col>
                      <Col md="3">
                        <Label htmlFor="experienceLetter">Experience Letter</Label>
                        <DragAndDropComponent
                          initialFile={companyFiles[0]?.experienceLetter}
                          handleFileUpload={(response) => setCompanyFiles((prev) => [{ ...prev[0], experienceLetter: response }])}
                        />
                      </Col>
                      <Col md="3">
                        <Label htmlFor="educationalCertificates">Educational Certificates</Label>
                        <DragAndDropComponent
                          initialFile={uploadedDocs.educationalCertificates}
                          handleFileUpload={(response) => handleFileUpload(response, "educationalCertificates")}
                        />
                      </Col>
                      <Col md="3">
                        <Label htmlFor="nationalIdentification">National Identification</Label>
                        <DragAndDropComponent
                          initialFile={uploadedDocs.nationalIdentification}
                          handleFileUpload={(response) => handleFileUpload(response, "nationalIdentification")}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col md="3">
                        <Label htmlFor="resume">Resume</Label>
                        <DragAndDropComponent
                          initialFile={uploadedDocs.resume}
                          handleFileUpload={(response) => handleFileUpload(response, "resume")}
                        />
                      </Col>
                      <Col md="3">
                        <Label htmlFor="cancelledCheckbook">Cancelled Checkbook</Label>
                        <DragAndDropComponent
                          initialFile={uploadedDocs.cancelledCheckbook}
                          handleFileUpload={(response) => handleFileUpload(response, "cancelledCheckbook")}
                        />
                      </Col>
                    </Row>
                  </Row>
                </Form>
              </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
        <Footer save={validation.handleSubmit} />
      </div>
    );
}

export default AddEmployee;